import type { MetaFunction } from "@remix-run/cloudflare";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Separator } from "~/components/ui/separator";
import { FileText, User, Shield, AlertTriangle, Scale, Gavel, Eye, Ban } from "lucide-react";

export const meta: MetaFunction = () => {
  return [
    { title: "Terms of Use | Stellar Blade Mods" },
    { 
      name: "description", 
      content: "Read the Terms of Use for Stellar Blade Mods. These terms govern your use of our website and services, including account creation, content submission, and user responsibilities." 
    },
    { name: "keywords", content: "terms of use, stellar blade mods, user agreement, website terms, legal terms, mod community rules" },
    
    // Robots directive for legal pages
    { name: "robots", content: "index,follow" },
    
    // Canonical URL
    { tagName: "link", rel: "canonical", href: "https://stellarblademod.com/terms-of-use" },
    
    // Open Graph
    { property: "og:title", content: "Terms of Use | Stellar Blade Mods" },
    { property: "og:description", content: "Read the Terms of Use for Stellar Blade Mods website and services." },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://stellarblademod.com/terms-of-use" },
    
    // Twitter Card
    { name: "twitter:card", content: "summary" },
    { name: "twitter:title", content: "Terms of Use | Stellar Blade Mods" },
    { name: "twitter:description", content: "Read the Terms of Use for Stellar Blade Mods website and services." },
  ];
};

export default function TermsOfUse() {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <div className="relative text-white py-20 border-b border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-8">
              <div className="rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 p-6 backdrop-blur-sm border border-white/10">
                <Scale className="h-16 w-16 text-blue-400" />
              </div>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Terms of Use
            </h1>
            
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Welcome to Stellar Blade Mods. These terms govern your use of our website and services. 
              Please read them carefully before using our platform.
            </p>
            
            <div className="flex items-center justify-center gap-6 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span>Legal Agreement</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <span>User Protection</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-16">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 p-4 bg-yellow-900/20 border border-yellow-700/30 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-400 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-300 font-medium mb-1">Important Notice</p>
                <p className="text-sm text-slate-300">
                  By using this website, you agree to be bound by these Terms of Use. 
                  Last updated on {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {/* Introduction */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <FileText className="h-6 w-6 text-blue-400" />
                  Introduction
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  Welcome and thank you for using the Stellar Blade Mods website ("Site"). These Terms of Use 
                  govern your use of the Site, and by using the Site you agree to be bound by these terms and 
                  consent to the information collection described in our Privacy Policy.
                </p>
                <p>
                  These terms constitute a legally binding agreement between you and Stellar Blade Mods. 
                  If you do not agree to these terms, please do not use our website or services.
                </p>
              </CardContent>
            </Card>

            {/* Account Creation */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <User className="h-6 w-6 text-green-400" />
                  Account Creation and Management
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <h3 className="text-lg font-semibold text-white">Account Creation</h3>
                <p>
                  You may be required to create a user account ("Account") in order to use or access certain 
                  aspects of the Site. You agree that all of the details you provide in connection with your 
                  Account are about yourself and not about another individual.
                </p>
                
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-600">
                  <p className="text-sm text-slate-400 mb-2">When creating an account, you must:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Provide accurate and complete information</li>
                    <li>Maintain the security of your account credentials</li>
                    <li>Be at least 13 years of age (or age of majority in your jurisdiction)</li>
                    <li>Use your own identity and not impersonate others</li>
                  </ul>
                </div>

                <Separator className="bg-slate-600" />

                <h3 className="text-lg font-semibold text-white">Account Security</h3>
                <p>
                  You may not use anyone else's Account at any time and you may not allow anyone else to use 
                  your Account at any time. You are responsible for all activity occurring under your Account, 
                  including all activities or transactions conducted through the use of your Account.
                </p>
                <p>
                  You are responsible for maintaining the confidentiality of your Account username and password, 
                  and agree not to disclose your username and password to anyone. You agree not to transfer, 
                  resell, or otherwise convey your Account or the right to use your Account to anyone.
                </p>
                
                <div className="bg-red-900/20 border border-red-700/30 p-4 rounded-lg">
                  <p className="text-sm text-red-300">
                    <strong>Security Notice:</strong> If you have reason to believe that your Account is no 
                    longer secure, you must promptly change your password and immediately notify us through 
                    our contact page.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Account Suspension */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Ban className="h-6 w-6 text-red-400" />
                  Account Suspension and Termination
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  You agree that Stellar Blade Mods has the right, in our sole discretion, to investigate 
                  any actual or suspected violation of these Terms of Use and to suspend or terminate your 
                  Account and refuse you access to your Account or the Site for any reason, including if 
                  Stellar Blade Mods believes that you have violated these Terms of Use or any applicable law.
                </p>
                
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-600">
                  <p className="text-sm text-slate-400 mb-2">Reasons for account suspension or termination may include:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Violation of these Terms of Use</li>
                    <li>Posting inappropriate or harmful content</li>
                    <li>Copyright infringement or intellectual property violations</li>
                    <li>Harassment or abuse of other users</li>
                    <li>Attempting to circumvent site security measures</li>
                    <li>Any activity that we deem harmful to the community</li>
                  </ul>
                </div>
                
                <p>
                  You may also terminate your Account and your agreement to these terms at any time through 
                  our contact page. Upon termination, your right to use the Site will cease immediately.
                </p>
              </CardContent>
            </Card>

            {/* User Content */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <FileText className="h-6 w-6 text-purple-400" />
                  User Content and Submissions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <h3 className="text-lg font-semibold text-white">Content Submission</h3>
                <p>
                  Certain aspects of the Site may allow you to submit or post a variety of content to the Site, 
                  such as text (including comments), images, videos, mod files, and other content, either directly 
                  to the Service or through a Third Party Service (collectively, "User Content").
                </p>
                
                <div className="bg-blue-900/20 border border-blue-700/30 p-4 rounded-lg">
                  <p className="text-sm text-blue-300">
                    <strong>Content License:</strong> By submitting or posting User Content to the Service, 
                    you grant Stellar Blade Mods a royalty-free, perpetual, irrevocable, non-exclusive, 
                    sublicensable, assignable, unrestricted, worldwide license to use the User Content 
                    for the operation and improvement of our services.
                  </p>
                </div>

                <h3 className="text-lg font-semibold text-white">Content Responsibilities</h3>
                <p>
                  Please be aware that User Content is not confidential and may be accessible by other users 
                  and the public. You are solely responsible for your User Content. Please retain copies of 
                  all User Content as Stellar Blade Mods is under no obligation to store or return any User 
                  Content to you.
                </p>
                
                <div className="space-y-3">
                  <h4 className="font-semibold text-white">You represent and warrant the following:</h4>
                  <div className="space-y-2">
                    <div className="border-l-4 border-green-400 pl-4">
                      <p className="text-sm">Your User Content does not infringe, violate, or misappropriate any third-party intellectual property rights, including copyrights or trademarks.</p>
                    </div>
                    <div className="border-l-4 border-blue-400 pl-4">
                      <p className="text-sm">Your User Content will not violate any applicable laws or regulations or infringe or violate any rights of a third party.</p>
                    </div>
                    <div className="border-l-4 border-yellow-400 pl-4">
                      <p className="text-sm">Your User Content does not contain harmful, malicious, or inappropriate material.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Copyright */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Shield className="h-6 w-6 text-orange-400" />
                  Copyright and Intellectual Property
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <h3 className="text-lg font-semibold text-white">Copyright Protection</h3>
                <p>
                  If you believe that any User Content or other material on the Service infringes your copyright 
                  rights, please contact us with the exact URL of where the alleged infringing material is located. 
                  If we are notified that any User Content infringes another's intellectual property rights, we 
                  may remove such User Content.
                </p>
                
                <div className="bg-orange-900/20 border border-orange-700/30 p-4 rounded-lg">
                  <p className="text-sm text-orange-300">
                    <strong>Repeat Infringer Policy:</strong> We have a repeat infringer policy and reserve the 
                    right to terminate your Account for submitting infringing User Content in violation of these 
                    Terms of Use once or on a repeated basis.
                  </p>
                </div>

                <h3 className="text-lg font-semibold text-white">Stellar Blade Intellectual Property</h3>
                <p>
                  This website and its content are dedicated to supporting the Stellar Blade game community 
                  through user-generated modifications. We respect the intellectual property rights of SHIFT UP 
                  Corporation and Sony Interactive Entertainment, the creators and publishers of Stellar Blade.
                </p>
                
                <p>
                  All mods and content on this site are created by community members for non-commercial, 
                  educational, and entertainment purposes under fair use principles.
                </p>
              </CardContent>
            </Card>

            {/* Prohibited Uses */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <AlertTriangle className="h-6 w-6 text-red-400" />
                  Prohibited Uses
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>You may not use our Site for any unlawful purpose or in violation of these Terms. Prohibited uses include:</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-red-900/20 border border-red-700/30 p-4 rounded-lg">
                    <h4 className="font-semibold text-red-300 mb-2">Content Violations</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Posting illegal or harmful content</li>
                      <li>Copyright infringement</li>
                      <li>Malicious software distribution</li>
                      <li>Adult or inappropriate content</li>
                    </ul>
                  </div>
                  
                  <div className="bg-red-900/20 border border-red-700/30 p-4 rounded-lg">
                    <h4 className="font-semibold text-red-300 mb-2">Behavioral Violations</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Harassment or abuse of users</li>
                      <li>Spam or unsolicited content</li>
                      <li>Impersonation of others</li>
                      <li>Circumventing security measures</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Disclaimers */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Eye className="h-6 w-6 text-gray-400" />
                  Disclaimer of Warranties
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  Your use of the Site is at your own risk, and the Site is provided "as is" without warranties 
                  of any kind, either express or implied. Stellar Blade Mods does not warrant that the service 
                  will be available, uninterrupted, secure, or error-free, that defects will be corrected, or 
                  that the service or the servers that make the service available are free of viruses or other 
                  harmful components.
                </p>
                
                <div className="bg-gray-900/50 border border-gray-700/30 p-4 rounded-lg">
                  <p className="text-sm text-gray-300">
                    <strong>Important:</strong> Stellar Blade Mods does not accept any liability for any loss 
                    or damage arising from your use of the Site. This includes but is not limited to damage 
                    to your computer system, loss of data, or any other direct or indirect damages.
                  </p>
                </div>

                <h3 className="text-lg font-semibold text-white">Mod Usage Disclaimer</h3>
                <p>
                  All mods available through our site are third-party creations. We do not guarantee their 
                  functionality, safety, or compatibility. Users install and use mods at their own risk. 
                  Always backup your game saves before installing any modifications.
                </p>
              </CardContent>
            </Card>

            {/* Changes to Terms */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Changes to These Terms</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  Stellar Blade Mods has the discretion to update these Terms of Use at any time. When we do, 
                  we will post a notification on the main page of our Site and update the "last updated" date. 
                  We encourage Users to frequently check this page for any changes.
                </p>
                
                <p>
                  You acknowledge and agree that it is your responsibility to review these Terms of Use 
                  periodically and become aware of modifications. Your continued use of the Site after 
                  changes are posted constitutes acceptance of the updated terms.
                </p>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Contact Us</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  If you have any questions about these Terms of Use or your dealings with the Site, 
                  please contact us:
                </p>
                
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-600">
                  <div className="space-y-2 text-sm">
                    <p><strong>Email:</strong> <span className="text-blue-300">contact@stellarblademod.com</span></p>
                    <p><strong>Contact Page:</strong> <a href="/contact" className="text-blue-300 hover:text-blue-400 transition-colors">/contact</a></p>
                    <p><strong>Discord:</strong> <a href="https://discord.gg/daYR38qPB2" target="_blank" rel="noopener noreferrer" className="text-blue-300 hover:text-blue-400 transition-colors">Join our community</a></p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Acceptance */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Acceptance of Terms</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  By accessing and using this website, you accept and agree to be bound by the terms and 
                  provision of this agreement. Additionally, when using this website's particular services, 
                  you shall be subject to any posted guidelines or rules applicable to such services.
                </p>
                
                <div className="bg-blue-900/20 border border-blue-700/30 p-4 rounded-lg">
                  <p className="text-sm text-blue-300">
                    Thank you for being part of the Stellar Blade Mods community. Together, we can create 
                    an amazing platform for sharing and discovering mods while respecting the rights of 
                    creators and the game's original developers.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
} 